import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PlusCircle, Pencil, Trash2, Lock, ExternalLink } from 'lucide-react';
import ConfirmModal from '../../utils/ConfirmModal';
import '../../../component-styles/RetainersDetail.css';
import defaultIfLogo from '../../../assets/default-if-logo.png';
import API_BASE from '../../../utils/api.js';
import notify from '../../../utils/notify';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

// Mirrors the non-funding paymentTypes excluded by isRetainerFundingPayment()
// in backend/src/routes/retainers.js — these are internal ledger entries
// (retainer applied to an invoice, manual refund, credit) and are never
// synced to QuickBooks on their own.
const INTERNAL_LEDGER_PAYMENT_TYPES = ['refund', 'application', 'retainer', 'credit'];

function isInternalLedgerEntry(payment) {
  const paymentType = String(payment?.paymentType || payment?.type || '')
    .trim()
    .toLowerCase();
  return INTERNAL_LEDGER_PAYMENT_TYPES.includes(paymentType);
}

function getQuickBooksPaymentStatus(payment) {
  if (isInternalLedgerEntry(payment)) return 'N/A';
  if (!payment?.quickbooks) return 'Not Synced';
  if (payment.quickbooks.status === 'created' || payment.quickbooks.invoiceId || payment.quickbooks.salesReceiptId) {
    return 'Synced';
  }
  if (payment.quickbooks.status === 'skipped') {
    return 'Skipped';
  }
  if (payment.quickbooks.status === 'failed') {
    return 'Failed';
  }
  return 'Pending';
}

function getQuickBooksInvoiceUrl(invoiceId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/invoice?txnId=${invoiceId}`;
}

// Legacy retainer payments synced to QuickBooks before the switch to Invoice+Payment
// were created as Sales Receipts — kept so their "View in QuickBooks" link still works.
function getQuickBooksSalesReceiptUrl(salesReceiptId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/salesreceipt?txnId=${salesReceiptId}`;
}

const MANUAL_METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'other', label: 'Other' },
];

export default function RetainerDetail() {
  const { projectId, retainerId } = useParams();
  const userId = localStorage.getItem('userId');

  const [project, setProject] = useState(null);
  const [retainer, setRetainer] = useState(null);
  const [syncingQuickBooks, setSyncingQuickBooks] = useState(false);

  const [showManual, setShowManual] = useState(false);

  const [manualForm, setManualForm] = useState({
    amount: '',
    method: 'bank_transfer',
    paymentDate: new Date().toISOString().slice(0, 10),
    reference: '',
  });

  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', method: 'bank_transfer', paymentDate: '', reference: '' });
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(null);

  const loadRetainer = async () => {
    try {
      const retResponse = await fetch(`${API_BASE}/api/retainers/${projectId}/${retainerId}`, {
        headers: { userid: userId },
      });
      const retData = await retResponse.json();
      retData.paymentHistory = retData.paymentHistory || [];
      setRetainer(retData);
    } catch (err) {}
  };

  useEffect(() => {
    async function load() {
      await loadRetainer();
      try {
        const projResp = await fetch(`${API_BASE}/api/projects/${userId}`);
        const projList = await projResp.json();
        const proj = projList.find((p) => p.projectId === projectId);
        setProject(proj);
      } catch (err) {}
    }

    load();
  }, [projectId, retainerId, userId, API_BASE]);

  const applyManualPayment = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/retainers/${projectId}/${retainerId}/apply-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', userid: userId },
          body: JSON.stringify({
            userId,
            amount: Number(manualForm.amount),
            paymentType: 'manual',
            method: manualForm.method,
            paymentDate: manualForm.paymentDate,
            reference: manualForm.reference,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) {
        notify(data.error || 'Failed to record payment');
        return;
      }

      setRetainer((prev) => ({
        ...prev,
        payments: data.newPayments,
        availableBalance: data.newBalance,
        paymentHistory: [...prev.paymentHistory, data.paymentRecord],
      }));

      if (data.quickbooksPayment?.status === 'failed') {
        notify(
          `Payment was recorded, but QuickBooks sync failed: ${
            data.quickbooksPayment.error || 'Unknown QuickBooks error'
          }`
        );
      }

      setShowManual(false);
      setManualForm({
        amount: '',
        method: 'bank_transfer',
        paymentDate: new Date().toISOString().slice(0, 10),
        reference: '',
      });
    } catch (err) {}
  };

  function openEditPayment(p) {
    setEditPayment(p);
    setEditForm({
      amount: String(p.amount),
      method: p.method || p.paymentType || 'bank_transfer',
      paymentDate: p.date ? p.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      reference: p.reference || '',
    });
  }

  async function saveEditPayment() {
    const amt = Number(editForm.amount);
    if (!amt || amt <= 0) return;

    const res = await fetch(
      `${API_BASE}/api/retainers/${projectId}/${retainerId}/payments/${editPayment.paymentId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', userid: userId },
        body: JSON.stringify({ amount: amt, method: editForm.method, reference: editForm.reference, paymentDate: editForm.paymentDate }),
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) { notify(data.error || 'Failed to update payment'); return; }

    setEditPayment(null);
    setRetainer((prev) => ({
      ...prev,
      payments: data.payments,
      availableBalance: data.availableBalance,
      paymentHistory: prev.paymentHistory.map((p) =>
        p.paymentId === editPayment.paymentId ? { ...p, ...data.payment } : p
      ),
    }));
  }

  async function deletePaymentEntry(paymentId) {
    const res = await fetch(
      `${API_BASE}/api/retainers/${projectId}/${retainerId}/payments/${paymentId}`,
      { method: 'DELETE', headers: { userid: userId } }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) { notify(data.error || 'Failed to delete payment'); return; }

    setRetainer((prev) => ({
      ...prev,
      payments: data.payments,
      availableBalance: data.availableBalance,
      paymentHistory: prev.paymentHistory.filter((p) => p.paymentId !== paymentId),
    }));
  }

  const handleSyncQuickBooks = async () => {
    try {
      setSyncingQuickBooks(true);

      const res = await fetch(
        `${API_BASE}/api/retainers/${projectId}/${retainerId}/sync-quickbooks`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            userid: userId,
          },
          body: JSON.stringify({ userId }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to sync retainer to QuickBooks.');
        return;
      }

      const refreshed = await fetch(`${API_BASE}/api/retainers/${projectId}/${retainerId}`, {
        headers: { userid: userId },
      });
      const refreshedData = await refreshed.json();
      refreshedData.paymentHistory = refreshedData.paymentHistory || [];
      setRetainer(refreshedData);

      const failedEntry = (refreshedData.paymentHistory || []).find(
        (entry) => entry?.quickbooks?.status === 'failed'
      );

      if (failedEntry?.quickbooks?.error) {
        notify(`QuickBooks sync failed: ${failedEntry.quickbooks.error}`);
        return;
      }

      notify(
        `QuickBooks sync complete. Synced ${data.syncedCount || 0}, failed ${data.failedCount || 0}, skipped ${data.skippedCount || 0}.`
      );
    } catch (err) {
      notify('Failed to sync retainer to QuickBooks.');
    } finally {
      setSyncingQuickBooks(false);
    }
  };

  if (!retainer || !project) return <div>Loading…</div>;
  retainer.paymentHistory = retainer.paymentHistory.filter(Boolean);
  const total = Number(retainer.amount || 0);
  const payments = Number(retainer.payments || 0);
  const remainingToFund = Math.max(total - payments, 0);

  const status =
    payments >= total && total > 0 ? 'Paid' : payments > 0 ? 'Partial' : 'Open';

  return (
    <div className="rd-container">
      <div className="rd-header-grid">
        <div className="rd-company-section">
          <img
            src={project.coverImageUrl || defaultIfLogo}
            alt="Company"
            className="rd-company-logo"
          />
          <div className="rd-company-info">
            <h3 className="rd-company-name">{project.projectNickname}</h3>
            <p className="rd-company-sub">
              {project.customerName}
              <br />
              {project.billingStreet}
              <br />
              {project.billingCity}, {project.billingState} {project.billingZip}
            </p>
          </div>
        </div>

        <div className="rd-meta-section">
          <div className="rd-meta-label">Retainer Number</div>
          <div className="rd-meta-value">{retainer.retainerNumber}</div>
          <div className="rd-meta-date">{retainer.requestDate}</div>

          <div
            className={`rd-status-pill ${
              status === 'Paid'
                ? 'rd-status-paid'
                : status === 'Partial'
                  ? 'rd-status-partial'
                  : 'rd-status-open'
            }`}
          >
            {status}
          </div>
        </div>
      </div>

      <table className="rd-items-table">
        <thead>
          <tr>
            <th>Items</th>
            <th>Description</th>
            <th className="rd-col-sm">Qty</th>
            <th className="rd-col-sm">Rate</th>
            <th className="rd-col-sm">Total Due</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              <div className="rd-item-img-wrapper">
                <img
                  src={project.coverImageUrl || defaultIfLogo}
                  alt="Company"
                  className="rd-company-logo"
                />
              </div>
            </td>
            <td>{retainer.description}</td>
            <td>1</td>
            <td>{formatCurrency(total)}</td>
            <td>{formatCurrency(remainingToFund)}</td>
          </tr>
        </tbody>
      </table>

      <div className="rd-totals-box">
        <div className="rd-totals-row">
          <span>Subtotal</span>
          <span>{formatCurrency(total)}</span>
        </div>

        <div className="rd-totals-row">
          <span>Taxes</span>
          <span>$0.00</span>
        </div>

        <div className="rd-totals-row rd-total">
          <span>Total Due</span>
          <span>{formatCurrency(remainingToFund)}</span>
        </div>

        {remainingToFund > 0 && (
          <button className="rd-pay-btn" onClick={() => setShowManual(true)}>
            <PlusCircle size={18} style={{ marginRight: 6 }} />
            Log a Payment
          </button>
        )}

        {remainingToFund <= 0 && (
          <div className="rd-retainer-funded-note">Retainer fully funded</div>
        )}

        <button
          className="rd-pay-btn"
          onClick={handleSyncQuickBooks}
          disabled={syncingQuickBooks || !retainer.paymentHistory.length}
          style={{ marginTop: 10 }}
        >
          {syncingQuickBooks ? 'Syncing…' : 'Sync to QuickBooks'}
        </button>
      </div>

      <div className="rd-payments-section">
        <h3>Payments</h3>

        {retainer.paymentHistory.length === 0 ? (
          <div className="rd-empty">No payments yet.</div>
        ) : (
          <table className="rd-payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Amount</th>
                <th>QuickBooks</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {retainer.paymentHistory.map((p) => (
                <tr key={p.paymentId}>
                  <td>{p.date ? p.date.slice(0, 10) : '—'}</td>
                  <td>{p.method || p.paymentType}</td>
                  <td>{p.reference || '—'}</td>
                  <td>{formatCurrency(p.amount)}</td>
                  <td title={p.quickbooks?.error || ''}>
                    {getQuickBooksPaymentStatus(p)}
                    {isInternalLedgerEntry(p) ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                        Internal ledger entry — reflected in QuickBooks via the linked invoice's sync, not synced separately.
                        {p.appliedTo?.docType === 'invoice' && p.appliedTo?.docId && (
                          <div style={{ marginTop: 4 }}>
                            <Link
                              to={`/projects/${projectId}/invoices/${p.appliedTo.docId}`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              View Invoice <ExternalLink size={11} />
                            </Link>
                          </div>
                        )}
                      </div>
                    ) : p.quickbooks?.status === 'failed' && p.quickbooks?.error ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#a33' }}>
                        {p.quickbooks.error}
                      </div>
                    ) : p.quickbooks?.status === 'skipped' && p.quickbooks?.reason ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                        {p.quickbooks.reason === 'retainer_balance_reduced_or_refunded'
                          ? 'Skipped because this funding was later reduced or refunded.'
                          : p.quickbooks.reason === 'before_sync_start_date'
                            ? 'Not sent: dated before your QuickBooks sync start date.'
                            : p.quickbooks.reason}
                      </div>
                    ) : null}
                    {(p.quickbooks?.invoiceId || p.quickbooks?.salesReceiptId) && p.quickbooks?.environment && (
                      <div style={{ marginTop: 4 }}>
                        <a
                          href={
                            p.quickbooks?.invoiceId
                              ? getQuickBooksInvoiceUrl(p.quickbooks.invoiceId, p.quickbooks.environment)
                              : getQuickBooksSalesReceiptUrl(p.quickbooks.salesReceiptId, p.quickbooks.environment)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                        >
                          View in QuickBooks <ExternalLink size={11} />
                        </a>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.paymentType === 'stripe' ? (
                      <Lock size={13} style={{ color: '#aaa', verticalAlign: 'middle' }} title="Stripe payment — locked" />
                    ) : (
                      <>
                        <button className="rd-icon-btn" title="Edit payment" onClick={() => openEditPayment(p)} style={{ marginRight: 4 }}>
                          <Pencil size={13} />
                        </button>
                        <button className="rd-icon-btn rd-icon-btn--danger" title="Delete payment" onClick={() => setConfirmDeletePayment(p)}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showManual && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setShowManual(false)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Record Manual Payment</h3>

            <label>Amount</label>
            <input
              type="number"
              value={manualForm.amount}
              onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
            />

            <label>Method</label>
            <select
              value={manualForm.method}
              onChange={(e) => setManualForm({ ...manualForm, method: e.target.value })}
            >
              {MANUAL_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <label>Payment Date</label>
            <input
              type="date"
              value={manualForm.paymentDate}
              onChange={(e) => setManualForm({ ...manualForm, paymentDate: e.target.value })}
            />

            <label>Reference</label>
            <input
              type="text"
              value={manualForm.reference}
              onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
            />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setShowManual(false)}>
                Cancel
              </button>
              <button className="rd-save" onClick={applyManualPayment}>
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {editPayment && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setEditPayment(null)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Payment</h3>

            <label>Amount</label>
            <input type="number" step="0.01" value={editForm.amount}
              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} />

            <label>Method</label>
            <select value={editForm.method} onChange={(e) => setEditForm({ ...editForm, method: e.target.value })}>
              {MANUAL_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <label>Payment Date</label>
            <input type="date" value={editForm.paymentDate}
              onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })} />

            <label>Reference</label>
            <input type="text" value={editForm.reference}
              onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setEditPayment(null)}>Cancel</button>
              <button className="rd-save" onClick={saveEditPayment}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDeletePayment}
        title="Delete Payment"
        message={`Delete this ${confirmDeletePayment?.method || 'manual'} payment of ${formatCurrency(confirmDeletePayment?.amount)}? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const id = confirmDeletePayment.paymentId;
          setConfirmDeletePayment(null);
          deletePaymentEntry(id);
        }}
        onCancel={() => setConfirmDeletePayment(null)}
      />
    </div>
  );
}
